import { loadOpenCV } from "./opencvLoader";

const DEFAULT_OPTIONS = {
  // Keeping analysis bounded is important: the displayed 4032 x 3024 photos
  // otherwise produce hundreds of MB of temporary WASM matrices.
  maxAnalysisSize: 768,
  sensitivity: 25,
  minObjectAreaPct: 0.04,
  maxObjectAreaPct: 16,
  minLargeObjectAreaPct: 1.2,
  maxLargeObjectAreaPct: 55,
  maxRegions: 12,
  maxLargeRegions: 4,
  leftIsEarlier: null,
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

function safeDelete(value) {
  try {
    value?.delete?.();
  } catch (_) {
    // A failed OpenCV operation can invalidate a wrapper. Cleanup must never
    // turn a recoverable detection failure into a page-level error.
  }
}

function createMatScope() {
  const values = new Set();
  return {
    track(value) {
      if (value) values.add(value);
      return value;
    },
    release(value) {
      values.delete(value);
      return value;
    },
    dispose() {
      [...values].reverse().forEach(safeDelete);
      values.clear();
    },
  };
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.decoding = "async";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("One of the comparison images could not be loaded"));
    img.src = src;
  });
}

function imageToMat(cv, img, maxSize) {
  const naturalWidth = img.naturalWidth || img.width;
  const naturalHeight = img.naturalHeight || img.height;
  if (!naturalWidth || !naturalHeight) throw new Error("Comparison image has no dimensions");

  const scale = Math.min(1, maxSize / Math.max(naturalWidth, naturalHeight));
  const width = Math.max(1, Math.round(naturalWidth * scale));
  const height = Math.max(1, Math.round(naturalHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d", { alpha: false });
  if (!ctx) throw new Error("Canvas image analysis is unavailable");
  ctx.drawImage(img, 0, 0, width, height);

  return {
    mat: cv.imread(canvas),
    width,
    height,
    naturalWidth,
    naturalHeight,
  };
}

function sensitivityToThreshold(sensitivity) {
  return Math.round(clamp(53 - sensitivity * 0.48, 14, 48));
}

function describeObjectChange(presence, index, leftIsEarlier) {
  const n = index + 1;
  if (presence === "both") {
    return {
      leftLabel: `Change ${n}: modified area`,
      rightLabel: `Change ${n}: modified area`,
      changeType: "modified",
    };
  }

  const rightOnly = presence === "right";
  if (typeof leftIsEarlier !== "boolean") {
    return {
      leftLabel: rightOnly
        ? `Change ${n}: not visible in left image`
        : `Change ${n}: only visible in left image`,
      rightLabel: rightOnly
        ? `Change ${n}: only visible in right image`
        : `Change ${n}: not visible in right image`,
      changeType: "side-only",
    };
  }

  const presentSideIsLater = rightOnly ? leftIsEarlier : !leftIsEarlier;
  if (presentSideIsLater) {
    return {
      leftLabel: rightOnly
        ? `Change ${n}: previously not present`
        : `Change ${n}: added object`,
      rightLabel: rightOnly
        ? `Change ${n}: added object`
        : `Change ${n}: previously not present`,
      changeType: "added",
    };
  }

  return {
    leftLabel: rightOnly
      ? `Change ${n}: removed / no longer present`
      : `Change ${n}: object before removal`,
    rightLabel: rightOnly
      ? `Change ${n}: object before removal`
      : `Change ${n}: removed / no longer present`,
    changeType: "removed",
  };
}

function rectTouchesBorder(rect, width, height, margin = 5) {
  return (
    rect.x <= margin ||
    rect.y <= margin ||
    rect.x + rect.width >= width - margin ||
    rect.y + rect.height >= height - margin
  );
}

function padRect(rect, padding, width, height) {
  const x = Math.max(0, rect.x - padding);
  const y = Math.max(0, rect.y - padding);
  const right = Math.min(width, rect.x + rect.width + padding);
  const bottom = Math.min(height, rect.y + rect.height + padding);
  return { x, y, width: right - x, height: bottom - y };
}

function analysisRectToPct(rect, analysisW, analysisH) {
  return {
    xPct: clamp((rect.x / analysisW) * 100, 0, 100),
    yPct: clamp((rect.y / analysisH) * 100, 0, 100),
    wPct: clamp((rect.width / analysisW) * 100, 0.2, 100),
    hPct: clamp((rect.height / analysisH) * 100, 0.2, 100),
  };
}

function transformRectToRight(cv, rect, homographyInv, rightW, rightH) {
  const scope = createMatScope();
  try {
    const corners = [
      rect.x,
      rect.y,
      rect.x + rect.width,
      rect.y,
      rect.x + rect.width,
      rect.y + rect.height,
      rect.x,
      rect.y + rect.height,
    ];
    const src = scope.track(cv.matFromArray(4, 1, cv.CV_32FC2, corners));
    const dst = scope.track(new cv.Mat());
    cv.perspectiveTransform(src, dst, homographyInv);

    const points = [];
    for (let i = 0; i < 4; i += 1) {
      const x = dst.floatAt(i, 0);
      const y = dst.floatAt(i, 1);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
      points.push({ x, y });
    }

    const rawMinX = Math.min(...points.map((point) => point.x));
    const rawMinY = Math.min(...points.map((point) => point.y));
    const rawMaxX = Math.max(...points.map((point) => point.x));
    const rawMaxY = Math.max(...points.map((point) => point.y));
    if (rawMaxX <= 0 || rawMaxY <= 0 || rawMinX >= rightW || rawMinY >= rightH) {
      return null;
    }

    const minX = clamp(rawMinX, 0, rightW);
    const minY = clamp(rawMinY, 0, rightH);
    const maxX = clamp(rawMaxX, 0, rightW);
    const maxY = clamp(rawMaxY, 0, rightH);
    if (maxX - minX < 2 || maxY - minY < 2) return null;

    return analysisRectToPct(
      { x: minX, y: minY, width: maxX - minX, height: maxY - minY },
      rightW,
      rightH
    );
  } finally {
    scope.dispose();
  }
}

function readHomography(homography) {
  const data = homography.data64F?.length
    ? homography.data64F
    : homography.data32F;
  return data && data.length >= 9 ? Array.from(data.slice(0, 9)) : null;
}

function homographyLooksSafe(homography, rightW, rightH, leftW, leftH) {
  if (!homography || homography.empty() || homography.rows !== 3 || homography.cols !== 3) {
    return false;
  }
  const h = readHomography(homography);
  if (!h || h.some((value) => !Number.isFinite(value))) return false;

  const determinant =
    h[0] * (h[4] * h[8] - h[5] * h[7]) -
    h[1] * (h[3] * h[8] - h[5] * h[6]) +
    h[2] * (h[3] * h[7] - h[4] * h[6]);
  if (!Number.isFinite(determinant) || Math.abs(determinant) < 1e-8) return false;

  const mapped = [
    [0, 0],
    [rightW, 0],
    [rightW, rightH],
    [0, rightH],
  ].map(([x, y]) => {
    const divisor = h[6] * x + h[7] * y + h[8];
    return {
      x: (h[0] * x + h[1] * y + h[2]) / divisor,
      y: (h[3] * x + h[4] * y + h[5]) / divisor,
    };
  });
  if (mapped.some(({ x, y }) => !Number.isFinite(x) || !Number.isFinite(y))) return false;

  const width = Math.max(...mapped.map((p) => p.x)) - Math.min(...mapped.map((p) => p.x));
  const height = Math.max(...mapped.map((p) => p.y)) - Math.min(...mapped.map((p) => p.y));
  return width > leftW * 0.35 && width < leftW * 2.5 && height > leftH * 0.35 && height < leftH * 2.5;
}

function alignImages(cv, leftGray, rightGray) {
  const scope = createMatScope();
  try {
    const orb = scope.track(new cv.ORB(1800));
    const leftKp = scope.track(new cv.KeyPointVector());
    const rightKp = scope.track(new cv.KeyPointVector());
    const leftDesc = scope.track(new cv.Mat());
    const rightDesc = scope.track(new cv.Mat());
    const featureMask = scope.track(new cv.Mat());

    orb.detectAndCompute(leftGray, featureMask, leftKp, leftDesc);
    orb.detectAndCompute(rightGray, featureMask, rightKp, rightDesc);
    if (leftKp.size() < 12 || rightKp.size() < 12 || leftDesc.empty() || rightDesc.empty()) {
      return null;
    }

    const matcher = scope.track(new cv.BFMatcher(cv.NORM_HAMMING, true));
    const matches = scope.track(new cv.DMatchVector());
    matcher.match(rightDesc, leftDesc, matches);

    const good = [];
    for (let i = 0; i < matches.size(); i += 1) {
      const match = matches.get(i);
      if (Number.isFinite(match.distance) && match.distance <= 64) good.push(match);
    }
    good.sort((a, b) => a.distance - b.distance);
    const picked = good.slice(0, Math.min(160, good.length));
    if (picked.length < 10) return null;

    const sourcePoints = [];
    const destinationPoints = [];
    picked.forEach((match) => {
      const rightPoint = rightKp.get(match.queryIdx).pt;
      const leftPoint = leftKp.get(match.trainIdx).pt;
      sourcePoints.push(rightPoint.x, rightPoint.y);
      destinationPoints.push(leftPoint.x, leftPoint.y);
    });

    const source = scope.track(
      cv.matFromArray(picked.length, 1, cv.CV_32FC2, sourcePoints)
    );
    const destination = scope.track(
      cv.matFromArray(picked.length, 1, cv.CV_32FC2, destinationPoints)
    );
    const homography = scope.track(
      cv.findHomography(source, destination, cv.RANSAC, 4.0)
    );

    // Validate before invert/warp. Passing an empty matrix into those native
    // calls is the crash that used to bring down compare mode.
    if (
      !homographyLooksSafe(
        homography,
        rightGray.cols,
        rightGray.rows,
        leftGray.cols,
        leftGray.rows
      )
    ) {
      return null;
    }

    const homographyInv = scope.track(new cv.Mat());
    const invertResult = cv.invert(homography, homographyInv, cv.DECOMP_LU);
    if (!invertResult || homographyInv.empty()) return null;

    return {
      homography: scope.release(homography),
      homographyInv: scope.release(homographyInv),
      matchCount: picked.length,
    };
  } finally {
    scope.dispose();
  }
}

function classifyRegion(cv, leftEdges, rightEdges, rect) {
  const scope = createMatScope();
  try {
    const left = scope.track(leftEdges.roi(rect));
    const right = scope.track(rightEdges.roi(rect));
    const area = Math.max(1, rect.width * rect.height);
    const leftDensity = cv.countNonZero(left) / area;
    const rightDensity = cv.countNonZero(right) / area;
    const delta = rightDensity - leftDensity;
    const meaningfulDelta = Math.max(0.012, Math.min(leftDensity, rightDensity) * 0.28);
    if (delta > meaningfulDelta) return "right";
    if (delta < -meaningfulDelta) return "left";
    return "both";
  } finally {
    scope.dispose();
  }
}

function overlapOverSmallerArea(a, b) {
  const width = Math.max(
    0,
    Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x)
  );
  const height = Math.max(
    0,
    Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y)
  );
  const intersection = width * height;
  const smallerArea = Math.min(a.width * a.height, b.width * b.height);
  return smallerArea ? intersection / smallerArea : 0;
}

function selectDistinctCandidates(candidates, limit, overlapThreshold = 0.45) {
  const selected = [];
  [...candidates]
    .sort((a, b) => b.score - a.score)
    .some((candidate) => {
      if (
        selected.every(
          (picked) =>
            overlapOverSmallerArea(candidate.rect, picked.rect) < overlapThreshold
        )
      ) {
        selected.push(candidate);
      }
      return selected.length >= limit;
    });
  return selected;
}

function selectMultiScaleCandidates(smallCandidates, largeCandidates, options) {
  const largeLimit = options.maxLargeRegions ?? 4;
  const totalLimit = options.maxRegions ?? 12;
  const pickedLarge = selectDistinctCandidates(largeCandidates, largeLimit, 0.35);
  const remaining = Math.max(0, totalLimit - pickedLarge.length);
  const smallFiltered = smallCandidates.filter((candidate) =>
    pickedLarge.every(
      (large) => overlapOverSmallerArea(candidate.rect, large.rect) < 0.55
    )
  );
  const pickedSmall = selectDistinctCandidates(smallFiltered, remaining);
  return [...pickedLarge, ...pickedSmall];
}

function extractCandidatesFromContours(
  cv,
  contours,
  combined,
  leftEdges,
  rightEdges,
  frameWidth,
  frameHeight,
  filters
) {
  const imageArea = frameWidth * frameHeight;
  const minArea = (filters.minAreaPct / 100) * imageArea;
  const maxArea = (filters.maxAreaPct / 100) * imageArea;
  const candidates = [];

  for (let i = 0; i < contours.size(); i += 1) {
    const contour = contours.get(i);
    try {
      const area = cv.contourArea(contour);
      if (area < minArea || area > maxArea) continue;
      const rawRect = cv.boundingRect(contour);
      const rectArea = rawRect.width * rawRect.height;
      if (!rectArea || area / rectArea < filters.minFillRatio) continue;

      const areaPct = (rectArea / imageArea) * 100;
      if (areaPct > filters.maxAreaPct) continue;
      if (
        rectTouchesBorder(rawRect, frameWidth, frameHeight) &&
        areaPct > filters.maxBorderAreaPct
      ) {
        continue;
      }
      const aspect = rawRect.width / Math.max(1, rawRect.height);
      if (aspect > filters.maxAspect || aspect < filters.minAspect) continue;

      const rect = padRect(rawRect, filters.padding, frameWidth, frameHeight);
      const diffRoi = combined.roi(rect);
      let meanDiff;
      try {
        meanDiff = cv.mean(diffRoi)[0];
      } finally {
        safeDelete(diffRoi);
      }
      if (meanDiff < filters.minMeanDiff) continue;

      const presence = classifyRegion(cv, leftEdges, rightEdges, rect);
      const touchesBorder = rectTouchesBorder(rect, frameWidth, frameHeight);
      const borderWeight =
        touchesBorder && areaPct > filters.largeBorderAreaPct
          ? filters.largeBorderWeight
          : touchesBorder
            ? filters.borderWeight
            : 1;
      candidates.push({
        rect,
        area,
        presence,
        score:
          meanDiff *
          Math.pow(Math.max(area, 1), filters.areaExponent) *
          (presence === "both" ? 1 : filters.sideOnlyBoost) *
          borderWeight,
      });
    } finally {
      safeDelete(contour);
    }
  }

  return candidates;
}

function detectObjectChanges(cv, leftFrame, rightFrame, options) {
  const scope = createMatScope();
  try {
    const leftGray = scope.track(new cv.Mat());
    const rightGray = scope.track(new cv.Mat());
    cv.cvtColor(leftFrame.mat, leftGray, cv.COLOR_RGBA2GRAY);
    cv.cvtColor(rightFrame.mat, rightGray, cv.COLOR_RGBA2GRAY);

    const alignment = alignImages(cv, leftGray, rightGray);
    if (!alignment) return { changes: [], alignmentFailed: true, matchCount: 0 };
    const homography = scope.track(alignment.homography);
    const homographyInv = scope.track(alignment.homographyInv);

    const size = new cv.Size(leftFrame.width, leftFrame.height);
    const warpedRightGray = scope.track(new cv.Mat());
    cv.warpPerspective(
      rightGray,
      warpedRightGray,
      homography,
      size,
      cv.INTER_LINEAR,
      cv.BORDER_CONSTANT,
      new cv.Scalar(0)
    );

    // Build and shrink a coverage mask so the black warp boundary is never
    // mistaken for a removed object.
    const rightCoverage = scope.track(
      new cv.Mat(rightFrame.height, rightFrame.width, cv.CV_8UC1, new cv.Scalar(255))
    );
    const validMask = scope.track(new cv.Mat());
    cv.warpPerspective(
      rightCoverage,
      validMask,
      homography,
      size,
      cv.INTER_NEAREST,
      cv.BORDER_CONSTANT,
      new cv.Scalar(0)
    );
    const coverageKernel = scope.track(
      cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(9, 9))
    );
    cv.erode(validMask, validMask, coverageKernel);

    // Blur away JPEG grain and sub-pixel registration noise before comparing.
    const leftSmooth = scope.track(new cv.Mat());
    const rightSmooth = scope.track(new cv.Mat());
    cv.GaussianBlur(leftGray, leftSmooth, new cv.Size(5, 5), 0);
    cv.GaussianBlur(warpedRightGray, rightSmooth, new cv.Size(5, 5), 0);
    const intensityDiff = scope.track(new cv.Mat());
    cv.absdiff(leftSmooth, rightSmooth, intensityDiff);

    const leftEdges = scope.track(new cv.Mat());
    const rightEdges = scope.track(new cv.Mat());
    const edgeDiff = scope.track(new cv.Mat());
    cv.Canny(leftSmooth, leftEdges, 55, 130);
    cv.Canny(rightSmooth, rightEdges, 55, 130);
    cv.absdiff(leftEdges, rightEdges, edgeDiff);

    const combined = scope.track(new cv.Mat());
    cv.addWeighted(intensityDiff, 0.82, edgeDiff, 0.18, 0, combined);
    const threshold = sensitivityToThreshold(options.sensitivity);
    const mask = scope.track(new cv.Mat());
    cv.threshold(combined, mask, threshold, 255, cv.THRESH_BINARY);
    cv.bitwise_and(mask, validMask, mask);

    const openKernel = scope.track(
      cv.getStructuringElement(cv.MORPH_ELLIPSE, new cv.Size(3, 3))
    );
    const closeKernel = scope.track(
      cv.getStructuringElement(cv.MORPH_ELLIPSE, new cv.Size(7, 7))
    );
    const cleaned = scope.track(new cv.Mat());
    cv.morphologyEx(mask, cleaned, cv.MORPH_OPEN, openKernel);
    cv.morphologyEx(cleaned, cleaned, cv.MORPH_CLOSE, closeKernel);

    // A second, coarser pass connects building-scale differences that the
    // detail-oriented mask fragments into many tiny blobs.
    const largeBlur = scope.track(new cv.Mat());
    cv.GaussianBlur(combined, largeBlur, new cv.Size(15, 15), 0);
    const largeMask = scope.track(new cv.Mat());
    cv.threshold(
      largeBlur,
      largeMask,
      Math.max(10, threshold - 8),
      255,
      cv.THRESH_BINARY
    );
    cv.bitwise_and(largeMask, validMask, largeMask);
    const largeOpenKernel = scope.track(
      cv.getStructuringElement(cv.MORPH_ELLIPSE, new cv.Size(5, 5))
    );
    const largeCloseKernel = scope.track(
      cv.getStructuringElement(cv.MORPH_ELLIPSE, new cv.Size(25, 25))
    );
    const largeCleaned = scope.track(new cv.Mat());
    cv.morphologyEx(largeMask, largeCleaned, cv.MORPH_OPEN, largeOpenKernel);
    cv.morphologyEx(largeCleaned, largeCleaned, cv.MORPH_CLOSE, largeCloseKernel);

    const contours = scope.track(new cv.MatVector());
    const hierarchy = scope.track(new cv.Mat());
    cv.findContours(cleaned, contours, hierarchy, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE);

    const largeContours = scope.track(new cv.MatVector());
    const largeHierarchy = scope.track(new cv.Mat());
    cv.findContours(
      largeCleaned,
      largeContours,
      largeHierarchy,
      cv.RETR_EXTERNAL,
      cv.CHAIN_APPROX_SIMPLE
    );

    const imageArea = leftFrame.width * leftFrame.height;
    const smallCandidates = extractCandidatesFromContours(
      cv,
      contours,
      combined,
      leftEdges,
      rightEdges,
      leftFrame.width,
      leftFrame.height,
      {
        minAreaPct: options.minObjectAreaPct,
        maxAreaPct: options.maxObjectAreaPct,
        minFillRatio: 0.09,
        maxBorderAreaPct: 2.5,
        maxAspect: 10,
        minAspect: 0.1,
        padding: 5,
        minMeanDiff: 0,
        areaExponent: 0.25,
        sideOnlyBoost: 2.25,
        borderWeight: 0.55,
        largeBorderAreaPct: 999,
        largeBorderWeight: 0.55,
      }
    );

    const largeCandidates = extractCandidatesFromContours(
      cv,
      largeContours,
      combined,
      leftEdges,
      rightEdges,
      leftFrame.width,
      leftFrame.height,
      {
        minAreaPct: options.minLargeObjectAreaPct,
        maxAreaPct: options.maxLargeObjectAreaPct,
        minFillRatio: 0.05,
        maxBorderAreaPct: 38,
        maxAspect: 14,
        minAspect: 0.07,
        padding: 8,
        minMeanDiff: threshold * 0.35,
        areaExponent: 0.42,
        sideOnlyBoost: 2.0,
        borderWeight: 0.7,
        largeBorderAreaPct: 8,
        largeBorderWeight: 0.88,
      }
    ).filter(
      (item) =>
        (item.rect.width * item.rect.height) / imageArea >=
        options.minLargeObjectAreaPct / 100
    );

    const picked = selectMultiScaleCandidates(smallCandidates, largeCandidates, options);

    const changes = picked.map((item, index) => {
      const leftBox = analysisRectToPct(item.rect, leftFrame.width, leftFrame.height);
      const rightBox =
        transformRectToRight(
          cv,
          item.rect,
          homographyInv,
          rightFrame.width,
          rightFrame.height
        ) || leftBox;
      return {
        id: `change-${index + 1}`,
        left: leftBox,
        right: rightBox,
        ...describeObjectChange(item.presence, index, options.leftIsEarlier),
        areaPct: (item.rect.width * item.rect.height * 100) / imageArea,
      };
    });

    return {
      changes,
      alignmentFailed: false,
      matchCount: alignment.matchCount,
    };
  } finally {
    scope.dispose();
  }
}

/** Align two overlapping photos and return object-sized change regions. */
export async function detectImageChanges(leftUrl, rightUrl, userOptions = {}) {
  const options = { ...DEFAULT_OPTIONS, ...userOptions };
  if (!leftUrl || !rightUrl) throw new Error("Two images are required for comparison");
  if (leftUrl === rightUrl) {
    return {
      changes: [],
      meta: { totalChanges: 0, alignmentFailed: false, matchCount: 0 },
    };
  }

  const { cv } = await loadOpenCV();
  const [leftImg, rightImg] = await Promise.all([loadImage(leftUrl), loadImage(rightUrl)]);
  const leftFrame = imageToMat(cv, leftImg, options.maxAnalysisSize);
  let rightFrame;
  try {
    rightFrame = imageToMat(cv, rightImg, options.maxAnalysisSize);
    const result = detectObjectChanges(cv, leftFrame, rightFrame, options);
    return {
      changes: result.changes,
      meta: {
        leftSize: { width: leftFrame.naturalWidth, height: leftFrame.naturalHeight },
        rightSize: { width: rightFrame.naturalWidth, height: rightFrame.naturalHeight },
        totalChanges: result.changes.length,
        alignmentFailed: result.alignmentFailed,
        matchCount: result.matchCount || 0,
      },
    };
  } finally {
    safeDelete(leftFrame.mat);
    safeDelete(rightFrame?.mat);
  }
}

export function getObjectFitContainRect(containerW, containerH, imgW, imgH) {
  if (!containerW || !containerH || !imgW || !imgH) {
    return { left: 0, top: 0, width: 0, height: 0 };
  }
  const scale = Math.min(containerW / imgW, containerH / imgH);
  const width = imgW * scale;
  const height = imgH * scale;
  return {
    left: (containerW - width) / 2,
    top: (containerH - height) / 2,
    width,
    height,
  };
}

export function changeBoxToStyle(box, imageRect) {
  if (!imageRect?.width || !imageRect?.height || !box) return null;
  return {
    left: `${imageRect.left + (box.xPct / 100) * imageRect.width}px`,
    top: `${imageRect.top + (box.yPct / 100) * imageRect.height}px`,
    width: `${(box.wPct / 100) * imageRect.width}px`,
    height: `${(box.hPct / 100) * imageRect.height}px`,
  };
}
